import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { EMPTY, Observable, catchError, defer, finalize, firstValueFrom, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { TokenService } from '@core/authorization/services/token.service';
import { StorageService } from '@core/storage/services/storage.service';
import { AuthorizationData, withRefreshTokenLock } from '@kaseya-shared';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { mfaTokenContext } from '@core/authorization/models/mfa-token-context';
import { RefreshTokenState } from '@core/authorization/models/refresh-token-state.enum';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { asNonNull } from '@kaseya-utils';
import { SessionExpirationService } from '@core/authorization/services/session-expiration.service';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly tokenService = inject(TokenService);
  private readonly storageService = inject(StorageService);
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly sessionExpirationService = inject(SessionExpirationService);

  private refreshInProgress$: Observable<void> | null = null;
  private state: RefreshTokenState = RefreshTokenState.Idle;

  constructor() {
    this.subscribeToLoginEvents();
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isMfaTokenRequest(request)) {
      return next.handle(request);
    }

    // Captured at dispatch: a mounted application may store a new token before this request's 401 arrives.
    const accessTokenBeforeRequest = this.storageService.getAuthorizationData()?.accessToken;

    return next.handle(request).pipe(
      catchError(error => {
        if (this.isUnauthorizedError(error)) {
          return this.handleUnauthorizedError(request, next, accessTokenBeforeRequest);
        }

        return throwError(() => error);
      })
    );
  }

  private handleUnauthorizedError(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    accessTokenBeforeRequest: string | undefined
  ): Observable<HttpEvent<unknown>> {
    switch (this.state) {
      case RefreshTokenState.Idle:
        return this.attemptTokenRefresh(request, next, accessTokenBeforeRequest);
      case RefreshTokenState.TokenRefreshInProgress:
        return this.waitForRefreshAndRetry(request, next);
      case RefreshTokenState.TokenRefreshFailed:
        return throwError(() => new HttpErrorResponse({ status: HttpStatusCode.Unauthorized }));
      default:
        return EMPTY;
    }
  }

  private attemptTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    accessTokenBeforeRequest: string | undefined
  ): Observable<HttpEvent<unknown>> {
    const { accessToken, refreshToken } = this.storageService.getAuthorizationData() ?? {};

    if (!accessToken || !refreshToken) {
      this.performLogout();
      return EMPTY;
    }

    this.state = RefreshTokenState.TokenRefreshInProgress;
    this.refreshInProgress$ = defer(() => withRefreshTokenLock(() => this.refreshUnlessAlreadyRefreshed(accessTokenBeforeRequest))).pipe(
      tap(() => (this.state = RefreshTokenState.Idle)),
      catchError(error => this.handleRefreshFailure(error)),
      shareReplay(1),
      finalize(() => this.clearRefreshRequest())
    );

    return this.waitForRefreshAndRetry(request, next);
  }

  /** A mounted application may have refreshed the token while this request was waiting for the lock. */
  private async refreshUnlessAlreadyRefreshed(expiredAccessToken: string | undefined): Promise<void> {
    const storedAuthData = this.storageService.getAuthorizationData();

    if (!storedAuthData) {
      throw new HttpErrorResponse({ status: HttpStatusCode.Unauthorized });
    }

    if (storedAuthData.accessToken !== expiredAccessToken) {
      return;
    }

    const authData = await firstValueFrom(this.tokenService.refreshToken(storedAuthData.refreshToken));
    this.handleRefreshSuccess(authData);
  }

  private waitForRefreshAndRetry(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return asNonNull(this.refreshInProgress$).pipe(switchMap(() => next.handle(request)));
  }

  private handleRefreshSuccess(authData: AuthorizationData): void {
    this.storageService.setAuthorizationData(authData);
    this.userInfoFacade.loadUserInfo();
  }

  private handleRefreshFailure(error: unknown): Observable<never> {
    this.performLogout();
    return throwError(() => error);
  }

  private clearRefreshRequest(): void {
    this.refreshInProgress$ = null;
  }

  private performLogout(): void {
    this.state = RefreshTokenState.TokenRefreshFailed;
    this.sessionExpirationService.expireSession();
  }

  private isMfaTokenRequest({ context }: HttpRequest<unknown>): boolean {
    return !!context.get(mfaTokenContext);
  }

  private isUnauthorizedError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized;
  }

  private subscribeToLoginEvents(): void {
    this.authorizationService.onLogin.subscribe(() => {
      this.state = RefreshTokenState.Idle;
    });
  }
}
